import React, { useState } from 'react';
import { Download, Loader2, Eye, Award, FileCheck2, ShieldCheck, HelpCircle, ExternalLink } from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';
import { QrCodeViewer } from '../../../../shared/components/QrCodeViewer.js';

interface CertificatePdfPreviewProps {
  book: Book;
  blobUrl: string | null;
  isGenerating: boolean;
  authError: string | null;
  isPublic?: boolean;
  onClearAuthError: () => void;
  onDownload: () => void;
}

export const CertificatePdfPreview: React.FC<CertificatePdfPreviewProps> = ({
  book,
  blobUrl,
  isGenerating,
  authError,
  isPublic = false,
  onClearAuthError,
  onDownload,
}) => {
  const [viewMode, setViewMode] = useState<'interactive' | 'pdf'>('interactive');

  const handleOpenInNewTab = () => {
    if (blobUrl) {
      const win = window.open(blobUrl, '_blank');
      if (win) {
        win.focus();
      } else {
        onDownload();
      }
    } else {
      onDownload();
    }
  };

  // High-Fidelity Custom HTML/CSS Certificate Content
  const interactiveCertificateContent = (
    <div
      className="relative w-full overflow-hidden bg-slate-900 rounded-2xl border border-slate-800 p-4 sm:p-6 md:p-8 flex items-center justify-center select-none shadow-2xl"
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Certificate Board */}
      <div className="relative w-full max-w-3xl bg-amber-50/5 border-[10px] border-double border-amber-500/50 p-6 sm:p-10 rounded-xl text-center text-white overflow-hidden shadow-inner flex flex-col justify-between min-h-[460px] sm:min-h-[520px]">
        {/* Background Elegant Overlay Patterns */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[radial-gradient(#d97706_1px,transparent_1px)] [background-size:16px_16px]"></div>
        <div className="absolute -top-16 -left-16 w-32 h-32 rounded-full border-4 border-amber-500/20 opacity-30"></div>
        <div className="absolute -bottom-16 -right-16 w-32 h-32 rounded-full border-4 border-amber-500/20 opacity-30"></div>

        {/* Header */}
        <div className="space-y-2 relative z-10">
          <div className="flex justify-center items-center gap-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-slate-950 flex items-center justify-center shadow-lg border-2 border-amber-200">
              <Award className="w-7 h-7" />
            </div>
          </div>
          <div>
            <h4 className="font-extrabold text-[11px] sm:text-xs tracking-widest text-amber-400 uppercase">
              REPUBLIK INDONESIA
            </h4>
            <h3 className="font-serif font-black text-sm sm:text-base md:text-lg text-slate-100 tracking-wide mt-0.5">
              SERTIFIKAT ELEKTRONIK REGISTRASI BUKU
            </h3>
            <div className="h-0.5 w-24 bg-gradient-to-r from-transparent via-amber-400 to-transparent mx-auto mt-2"></div>
            <p className="text-[10px] text-slate-400 uppercase tracking-widest font-mono mt-1">
              Nomor Registrasi Resmi: <strong className="text-amber-300 font-mono">{book.uibn}</strong>
            </p>
          </div>
        </div>

        {/* Content Body */}
        <div className="my-6 space-y-4 relative z-10">
          <p className="text-[11px] sm:text-xs text-slate-400 italic">
            Dengan ini menerangkan bahwa naskah buku di bawah ini telah terdaftar secara sah dan resmi pada Katalog Universal Indonesian Book Number (UIBN):
          </p>

          <div className="space-y-1">
            <span className="text-[10px] text-amber-500 uppercase font-black tracking-widest block">
              JUDUL NASKAH BUKU
            </span>
            <h2 className="font-serif font-black text-base sm:text-lg md:text-xl text-white tracking-wide leading-tight px-4 max-w-xl mx-auto">
              "{book.title}"
            </h2>
          </div>

          {/* Grid Metadata - break-words and whitespace-normal utilized to read full names */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 max-w-lg mx-auto text-left text-xs bg-slate-950/30 p-3.5 rounded-xl border border-slate-800">
            <div>
              <span className="text-[9px] text-slate-400 uppercase font-bold tracking-wider block">
                Penulis / Pengarang
              </span>
              <strong className="text-slate-200 block font-serif text-sm leading-relaxed break-words whitespace-normal">
                {book.authorName}
              </strong>
            </div>

            <div>
              <span className="text-[9px] text-slate-400 uppercase font-bold tracking-wider block">
                Penerbit Terdaftar
              </span>
              <strong className="text-slate-200 block text-sm leading-relaxed break-words whitespace-normal">
                {book.publisherName}
              </strong>
            </div>

            <div>
              <span className="text-[9px] text-slate-400 uppercase font-bold tracking-wider block">
                Tahun Penerbitan
              </span>
              <span className="text-slate-300 block font-mono">
                Tahun {book.publicationYear} • Edisi {book.edition || '1'}
              </span>
            </div>

            <div>
              <span className="text-[9px] text-slate-400 uppercase font-bold tracking-wider block">
                Klasifikasi Kategori
              </span>
              <span className="text-slate-300 block">
                {book.category}
              </span>
            </div>
          </div>
        </div>

        {/* Footer Signatures / QR Codes */}
        <div className="pt-4 border-t border-slate-800 flex items-center justify-between gap-4 flex-wrap relative z-10 text-left">
          {/* QR Code */}
          <div className="bg-white p-1.5 rounded-lg shrink-0 flex items-center justify-center shadow-md border border-slate-200">
            <QrCodeViewer uibn={book.uibn} size={68} showDownload={false} pure={true} />
          </div>

          {/* Center Seal Text */}
          <div className="text-[9px] text-slate-400 flex-1 space-y-1">
            <p className="font-mono">
              TEREGISTRASI: {new Date(book.createdAt).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            <div className="flex items-center gap-1 text-emerald-400 font-bold uppercase tracking-wider">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>TERVERIFIKASI SISTEM UIBN</span>
            </div>
          </div>

          {/* Golden Ribbon Stamp */}
          <div className="flex flex-col items-center justify-center text-center shrink-0">
            <div className="relative w-14 h-14 bg-gradient-to-tr from-amber-400 via-yellow-300 to-amber-500 rounded-full border-2 border-white shadow-md flex items-center justify-center">
              <Award className="w-8 h-8 text-slate-950" />
              {/* Decorative ribbon edges */}
              <div className="absolute -bottom-1 left-2 w-3 h-5 bg-amber-600 -rotate-12 rounded-xs shadow-xs -z-10"></div>
              <div className="absolute -bottom-1 right-2 w-3 h-5 bg-amber-600 rotate-12 rounded-xs shadow-xs -z-10"></div>
            </div>
            <span className="text-[8px] font-bold text-amber-400 uppercase tracking-widest mt-1">
              SEAL OF TRUST
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {authError && (
        <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center justify-between">
          <span>{authError}</span>
          <button
            type="button"
            onClick={onClearAuthError}
            className="font-bold text-rose-700 cursor-pointer"
          >
            Tutup
          </button>
        </div>
      )}

      {/* 
        If it is public, we completely hide the Mode Switcher bar.
        We directly show the beautifully interactive HTML Certificate.
      */}
      {!isPublic ? (
        <>
          {/* Mode Switcher for logged-in/verified users */}
          <div className="bg-slate-100 p-1.5 rounded-xl border border-slate-200 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setViewMode('interactive')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer min-h-[32px] ${
                  viewMode === 'interactive'
                    ? 'bg-white text-indigo-700 shadow-2xs border border-slate-200'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Eye className="w-3.5 h-3.5" />
                <span>Mockup Sertifikat</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('pdf')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer min-h-[32px] ${
                  viewMode === 'pdf'
                    ? 'bg-white text-indigo-700 shadow-2xs border border-slate-200'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Muat dokumen PDF mentah"
              >
                <FileCheck2 className="w-3.5 h-3.5" />
                <span>Format Cetak PDF</span>
              </button>
            </div>

            <span className="text-[10px] text-slate-500 font-semibold px-2 py-1 flex items-center gap-1">
              <HelpCircle className="w-3 h-3 text-slate-400" />
              <span>Pratinjau PDF resmi terverifikasi dan siap cetak</span>
            </span>
          </div>

          {viewMode === 'interactive' ? (
            interactiveCertificateContent
          ) : (
            /* Native PDF / Object Viewer with Action Bar */
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2 p-2.5 bg-slate-800 text-white rounded-xl text-xs flex-wrap shadow-xs">
                <div className="flex items-center gap-2">
                  <FileCheck2 className="w-4 h-4 text-amber-400 shrink-0" />
                  <span className="font-semibold text-slate-200">
                    Dokumen PDF Cetak Resmi (A4 Landscape)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleOpenInNewTab}
                    className="px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-100 font-medium text-[11px] inline-flex items-center gap-1.5 transition cursor-pointer"
                    title="Buka dokumen PDF di tab baru peramban"
                  >
                    <ExternalLink className="w-3.5 h-3.5 text-amber-300" />
                    <span>Buka PDF di Tab Baru</span>
                  </button>
                  <button
                    type="button"
                    onClick={onDownload}
                    className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-[11px] inline-flex items-center gap-1.5 transition cursor-pointer shadow-xs"
                    title="Unduh berkas PDF resmi ke perangkat"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Unduh PDF</span>
                  </button>
                </div>
              </div>

              <div
                className="relative w-full h-[480px] sm:h-[540px] bg-slate-100 rounded-xl overflow-hidden border border-slate-200 shadow-inner flex items-center justify-center select-none"
                onContextMenu={(e) => e.preventDefault()}
              >
                {isGenerating ? (
                  <div className="flex flex-col items-center gap-3 text-slate-500 text-sm">
                    <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                    <p className="font-medium">Memuat E-Sertifikat Digital Publik...</p>
                    <span className="text-xs text-slate-400">
                      Menyiapkan pratinjau dokumen A4 Landscape terverifikasi UIBN...
                    </span>
                  </div>
                ) : blobUrl ? (
                  <object
                    data={blobUrl}
                    type="application/pdf"
                    className="w-full h-full rounded-xl"
                  >
                    <iframe
                      src={blobUrl}
                      title={`Sertifikat UIBN - ${book.title}`}
                      className="w-full h-full border-none rounded-xl"
                    />
                  </object>
                ) : (
                  <div className="text-center p-6 text-slate-500 text-sm space-y-3">
                    <p className="text-slate-600">
                      Tidak dapat memuat berkas PDF langsung di peramban ini. Gunakan tombol di bawah ini untuk membuka atau mengunduh PDF resmi.
                    </p>
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                      <button
                        type="button"
                        onClick={handleOpenInNewTab}
                        className="px-4 py-2 rounded-lg bg-slate-800 text-white font-medium inline-flex items-center gap-2 cursor-pointer shadow-xs hover:bg-slate-700"
                      >
                        <ExternalLink className="w-4 h-4 text-amber-400" /> Buka PDF di Tab Baru
                      </button>
                      <button
                        type="button"
                        onClick={onDownload}
                        className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium inline-flex items-center gap-2 cursor-pointer shadow-xs hover:bg-indigo-700"
                      >
                        <Download className="w-4 h-4" /> Unduh Berkas PDF Resmi
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        /* Public View Mode - Directly render the HTML Interactive Certificate */
        interactiveCertificateContent
      )}
    </div>
  );
};
